import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  AlertTriangle,
  Minus,
  Wrench,
  RefreshCw
} from 'lucide-react';

/**
 * Helfer Inventar Seite
 * - Zeigt nur Werkstatt-Artikel
 * - Nur Entnahme möglich (kein Hinzufügen, Bearbeiten, Löschen)
 * - Kein Dashboard
 */
const HelperInventoryPage = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isHelper = user?.role === 'helper';
  const isAdmin = user?.role === 'admin';

  // Redirect Admin zur normalen Inventar-Seite
  useEffect(() => {
    if (isAdmin) {
      navigate('/inventar', { replace: true });
    }
  }, [isAdmin, navigate]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Withdraw Dialog State
  const [selectedItem, setSelectedItem] = useState(null);
  const [withdrawQuantity, setWithdrawQuantity] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isHelper) {
      fetchItems();
    }
  }, [isHelper]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/inventory`);
      // Backend filtert bereits auf Werkstatt für Helfer
      setItems(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Keine Berechtigung');
        navigate('/', { replace: true });
      } else {
        toast.error('Fehler beim Laden des Inventars');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sortierte und gefilterte Items (alphabetisch nach Name)
  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Alphabetische Sortierung nach Artikelname (A → Z)
    result.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'de');
    });
    
    // Suche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item => 
        (item.name || '').toLowerCase().includes(query) ||
        (item.code || '').toLowerCase().includes(query) ||
        (item.article_number || '').toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, searchQuery]);

  // Bestandsfarbe berechnen
  const getStockColor = (quantity) => {
    if (quantity <= 3) return 'text-red-600 bg-red-50';
    if (quantity <= 8) return 'text-orange-600 bg-orange-50';
    return 'text-green-600 bg-green-50';
  };

  const getStockBorderColor = (quantity) => {
    if (quantity <= 3) return 'border-red-200';
    if (quantity <= 8) return 'border-orange-200';
    return 'border-green-200';
  };

  // Prüfen ob Artikel nachbestellt werden muss
  const needsReorder = (item) => {
    const minStock = item.min_stock ?? 0;
    return item.quantity <= minStock;
  };

  // Kachel klicken → Entnahme-Dialog öffnen
  const handleCardClick = (item) => {
    setSelectedItem(item);
    setWithdrawQuantity('');
    setShowConfirmDialog(false);
  };

  // Entnahme bestätigen
  const handleWithdrawClick = () => {
    const qty = parseInt(withdrawQuantity);
    if (!qty || qty <= 0) {
      toast.error('Bitte gültige Stückzahl eingeben');
      return;
    }
    if (qty > selectedItem.quantity) {
      toast.error(`Nicht genug Bestand. Verfügbar: ${selectedItem.quantity}`);
      return;
    }
    setShowConfirmDialog(true);
  };

  // Entnahme durchführen
  const handleConfirmWithdraw = async () => {
    if (!selectedItem || isSubmitting) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/inventory/${selectedItem.id}/withdraw`, {
        quantity: parseInt(withdrawQuantity)
      });
      
      toast.success(`${withdrawQuantity} Stück entnommen. Neuer Bestand: ${response.data.stock_after}`);
      
      // Dialog schließen und Items neu laden
      setSelectedItem(null);
      setShowConfirmDialog(false);
      setWithdrawQuantity('');
      fetchItems();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error('Fehler bei der Entnahme');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dialog schließen
  const closeDialog = () => {
    setSelectedItem(null);
    setShowConfirmDialog(false);
    setWithdrawQuantity('');
  };

  if (isAdmin) {
    return null; // Redirect passiert in useEffect
  }

  if (!isHelper) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-slate-500">Keine Berechtigung</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden space-y-4 sm:space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 sm:mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Wrench className="text-amber-600" size={24} />
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Werkstatt Inventar
            </h1>
          </div>
          <p className="text-sm sm:text-base text-slate-600">
            Material entnehmen • Nur Werkstatt-Artikel
          </p>
        </div>
      </div>

      {/* Suchfeld */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Suche nach Artikel, Code, Artikelnummer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="text-sm text-slate-500">
            {filteredItems.length} {filteredItems.length === 1 ? 'Artikel' : 'Artikel'}
          </div>
        </div>
      </Card>

      {/* Kachel-Grid */}
      {filteredItems.length === 0 ? (
        <Card className="p-12">
          <div className="text-center text-slate-500">
            <Package size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg">Keine Werkstatt-Artikel gefunden</p>
            {searchQuery && (
              <p className="text-sm mt-2">
                Suche nach "{searchQuery}" ergab keine Treffer.{' '}
                <button 
                  onClick={() => setSearchQuery('')} 
                  className="text-blue-500 hover:underline"
                >
                  Suche zurücksetzen
                </button>
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card 
              key={item.id} 
              className={`p-4 hover:shadow-lg transition-shadow cursor-pointer flex flex-col min-h-[180px] ${getStockBorderColor(item.quantity)} border-2`}
              onClick={() => handleCardClick(item)}
            >
              {/* Header mit Code und Bestand */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-50 rounded-lg">
                    <Package className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="font-mono text-sm text-slate-500">{item.code}</p>
                  </div>
                </div>
                
                {/* Bestand-Badge rechts */}
                <div className={`px-3 py-2 rounded-lg font-bold text-2xl ${getStockColor(item.quantity)}`}>
                  {item.quantity}
                </div>
              </div>

              {/* Artikelname */}
              <h3 className="font-semibold text-lg text-slate-900 mb-2 line-clamp-2">
                {item.name}
              </h3>

              {/* Artikelnummer (falls vorhanden) */}
              {item.article_number && (
                <p className="text-sm text-slate-500 mb-1 break-all">
                  Art.-Nr.: {item.article_number}
                </p>
              )}

              {/* Nachbestellhinweis für Helfer */}
              {needsReorder(item) && (
                <div className="flex items-center gap-1.5 mt-2 text-orange-600">
                  <RefreshCw size={12} className="animate-spin-slow" />
                  <span className="text-xs font-medium">Artikel wird nachbestellt.</span>
                </div>
              )}

              {/* Spacer */}
              <div className="flex-1" />

              {/* Lagerort Badge */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <Wrench size={14} className="text-amber-500" />
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Werkstatt
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Entnahme Dialog */}
      <Dialog open={!!selectedItem && !showConfirmDialog} onOpenChange={closeDialog}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minus className="text-amber-600" size={20} />
              Material entnehmen
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              {/* Artikel-Info */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <p className="font-mono text-sm text-slate-500">{selectedItem.code}</p>
                <p className="font-semibold text-lg">{selectedItem.name}</p>
                {selectedItem.article_number && (
                  <p className="text-sm text-slate-500">Art.-Nr.: {selectedItem.article_number}</p>
                )}
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-slate-600">Aktueller Bestand:</span>
                  <span className={`font-bold text-xl ${getStockColor(selectedItem.quantity).split(' ')[0]}`}>
                    {selectedItem.quantity}
                  </span>
                </div>
              </div>

              {/* Eingabe */}
              <div>
                <Label htmlFor="withdraw-qty" className="text-base">Stückzahl entnehmen *</Label>
                <Input
                  id="withdraw-qty"
                  type="number"
                  min="1"
                  max={selectedItem.quantity}
                  value={withdrawQuantity}
                  onChange={(e) => setWithdrawQuantity(e.target.value)}
                  placeholder="z.B. 1"
                  className="text-lg mt-2"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">
                  Maximal {selectedItem.quantity} Stück verfügbar
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={closeDialog} className="w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button 
              onClick={handleWithdrawClick} 
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              disabled={!withdrawQuantity || parseInt(withdrawQuantity) <= 0}
            >
              <Minus size={16} className="mr-2" />
              Entnehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bestätigungs-Dialog (Einmalige Bestätigung) */}
      <Dialog open={showConfirmDialog} onOpenChange={() => setShowConfirmDialog(false)}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={20} />
              Buchung bestätigen
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <p className="font-semibold text-amber-800">
                Artikel wirklich buchen?
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Eine Änderung ist danach nicht mehr möglich!
              </p>
            </div>
            
            {selectedItem && (
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-500">Artikel:</span> {selectedItem.name}</p>
                <p><span className="text-slate-500">Code:</span> {selectedItem.code}</p>
                <p><span className="text-slate-500">Entnahme:</span> <strong>{withdrawQuantity} Stück</strong></p>
                <p><span className="text-slate-500">Bestand danach:</span> <strong>{selectedItem.quantity - parseInt(withdrawQuantity || 0)}</strong></p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)} 
              className="w-full sm:w-auto"
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleConfirmWithdraw} 
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Wird gebucht...' : 'Ja, jetzt buchen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelperInventoryPage;
